import { useRef, useState, useEffect } from 'react'

/**
 * Possible values for `getStatus` function
 */
export enum PollingStatus {
  STATUS_PENDING = 'STATUS_PENDING',
  STATUS_SUCCESS = 'STATUS_SUCCESS',
  STATUS_FAILURE = 'STATUS_FAILURE',
}

/**
 * Possible states the polling state machine is in
 */
export enum PollingState {
  INIT = 'INIT',
  STARTED = 'STARTED',
  PENDING = 'PENDING',
  SUCCESS = 'SUCCESS',
  FAILURE = 'FAILURE',
}

export type PollingArgs = {
  [key: string]: unknown
}

type TransactionStatus = 'completed' | 'failed' | 'pending'

type GetStatusProp = (
  args?: PollingArgs
) => Promise<{ status: TransactionStatus; errorMessage: string }>

interface FailureParams {
  reset: () => void
  error: Error
}

export interface UsePollingStateMachineProps {
  interval: number
  timeout?: number
  getStatus: GetStatusProp
  onSuccess?: () => void
  onFailure?: (failureParams: FailureParams) => void
  errorMessage: string
}

export interface PollingStateMachine {
  pollingState: PollingState
  start: () => void
  startPolling: (args?: PollingArgs) => void
  cleanup: () => void
  reset: () => void
}

/**
 * Polling state machine that calls `getStatus` on a given time interval
 *
 * @see {@link https://xstate.js.org/viz/?gist=6cffa9e950ab85ae3686dee906a26bd7 State machine diagram}
 */
export function usePollingStateMachine({
  interval,
  timeout,
  getStatus,
  onSuccess,
  onFailure,
  errorMessage,
}: UsePollingStateMachineProps): PollingStateMachine {
  const [pollingState, setPollingState] = useState<PollingState>(
    PollingState.INIT
  )
  const intervalTimer = useRef<ReturnType<typeof setInterval>>()
  const timeoutTimer = useRef<ReturnType<typeof setTimeout>>()

  const start = () => {
    setPollingState(PollingState.STARTED)
  }

  const startPolling = (args: PollingArgs) => {
    setPollingState(PollingState.PENDING)

    intervalTimer.current = setInterval(async () => {
      const result = await getStatus(args)

      if (result?.status === 'completed') {
        setPollingState(PollingState.SUCCESS)
        cleanup()

        if (onSuccess) {
          onSuccess()
        }
      } else if (result?.status === 'failed') {
        setPollingState(PollingState.FAILURE)
        cleanup()

        if (onFailure) {
          onFailure({
            reset,
            error: new Error(result.errorMessage || errorMessage),
          })
        }
      }
    }, interval)

    if (timeout !== undefined) {
      timeoutTimer.current = setTimeout(() => {
        setPollingState(PollingState.FAILURE)
        cleanup()

        const error = new Error('Timed out')
        if (onFailure) {
          onFailure({ reset, error })
        }
      }, timeout)
    }
  }

  const cleanup = () => {
    clearInterval(intervalTimer.current)
    clearTimeout(timeoutTimer.current)
    intervalTimer.current = null
    timeoutTimer.current = null
  }

  // Clean up on unmount
  useEffect(cleanup, [])

  const reset = () => {
    cleanup()
    setPollingState(PollingState.INIT)
  }

  return { pollingState, start, startPolling, cleanup, reset }
}
